import { UserFriendlyError } from '@affine/error';
import {
  gqlFetcherFactory,
  type OauthProvidersQuery,
  oauthProvidersQuery,
  type ServerBrandingQuery,
  serverBrandingQuery,
  type ServerBrandingExtrasQuery,
  serverBrandingExtrasQuery,
  type ServerConfigQuery,
  serverConfigQuery,
  ServerFeature,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';
import semver from 'semver';

export type ServerConfigType = ServerConfigQuery['serverConfig'] &
  Partial<ServerBrandingQuery['serverConfig']> &
  Partial<ServerBrandingExtrasQuery['serverConfig']> &
  OauthProvidersQuery['serverConfig'];

type ServerBranding = Partial<ServerBrandingQuery['serverConfig']> &
  Partial<ServerBrandingExtrasQuery['serverConfig']>;

export const DEFAULT_SERVER_BRANDING: ServerBranding = {
  defaultLanguage: 'en',
  showDownloadApp: false,
  showLearnMore: false,
};

function isValidBrandingUrl(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  const url = value.trim();
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

export function normalizeOfficialWebsiteUrl(
  value: unknown
): string | undefined {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }

  try {
    const url = new URL(value.trim());
    if (url.protocol !== 'https:' || url.username || url.password) {
      return undefined;
    }

    return url.href;
  } catch {
    return undefined;
  }
}

function normalizeOptionalBrandingText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  return value.trim() || undefined;
}

export function normalizeServerBranding(
  branding?: ServerBranding | null
): ServerBranding {
  const normalized: ServerBranding = {
    ...DEFAULT_SERVER_BRANDING,
  };
  const logoUrl = normalizeOptionalBrandingText(branding?.logoUrl);
  const subtitle = normalizeOptionalBrandingText(branding?.subtitle);
  const faviconUrl = normalizeOptionalBrandingText(branding?.faviconUrl);
  const officialWebsiteUrl = normalizeOfficialWebsiteUrl(
    branding?.officialWebsiteUrl
  );

  if (isValidBrandingUrl(logoUrl)) {
    normalized.logoUrl = logoUrl;
  }

  if (subtitle) {
    normalized.subtitle = subtitle;
  }

  if (officialWebsiteUrl) {
    normalized.officialWebsiteUrl = officialWebsiteUrl;
  }

  if (isValidBrandingUrl(faviconUrl)) {
    normalized.faviconUrl = faviconUrl;
  }

  if (
    branding?.defaultLanguage === 'zh-Hans' ||
    branding?.defaultLanguage === 'en'
  ) {
    normalized.defaultLanguage = branding.defaultLanguage;
  }

  if (typeof branding?.showDownloadApp === 'boolean') {
    normalized.showDownloadApp = branding.showDownloadApp;
  }

  if (typeof branding?.showLearnMore === 'boolean') {
    normalized.showLearnMore = branding.showLearnMore;
  }

  if (typeof branding?.showVersionUpdate === 'boolean') {
    normalized.showVersionUpdate = branding.showVersionUpdate;
  }

  return normalized;
}

export function mergeServerBranding(
  config: ServerConfigQuery['serverConfig'],
  branding?: ServerBranding
): ServerConfigType {
  return {
    ...config,
    ...normalizeServerBranding(branding),
    oauthProviders: [],
  } as ServerConfigType;
}

export const MIN_SUPPORTED_SERVER_VERSION = '0.27.0';

const NETWORK_ERROR_PATTERNS = [
  /failed to fetch/i,
  /network request failed/i,
  /fetch failed/i,
  /load failed/i,
  /networkerror/i,
  /cors/i,
  /certificate/i,
  /ssl/i,
  /err_[a-z_]+/i,
];

const MISSING_SERVER_VERSION_PATTERNS = [
  /cannot query field ["']?version["']? on type ["']?serverconfigtype["']?/i,
  /field ["']?version["']? is not defined by type ["']?serverconfigtype["']?/i,
];

export function createUnsupportedServerVersionError(version?: string | null) {
  const receivedVersion = version || 'unknown';
  return new UserFriendlyError({
    status: 426,
    code: 'UNSUPPORTED_SERVER_VERSION',
    type: 'UNSUPPORTED_SERVER_VERSION',
    name: 'UNSUPPORTED_SERVER_VERSION',
    message: `Unsupported server with version [${receivedVersion}], required version is [>=${MIN_SUPPORTED_SERVER_VERSION}].`,
    data: {
      serverVersion: receivedVersion,
      requiredVersion: `>=${MIN_SUPPORTED_SERVER_VERSION}`,
    },
  });
}

export function isSupportedServerVersion(version?: string | null) {
  const normalized = version && semver.valid(version, { loose: true });
  return (
    !!normalized &&
    semver.gte(normalized, `${MIN_SUPPORTED_SERVER_VERSION}-0`, {
      loose: true,
    })
  );
}

export function assertSupportedServerVersion(version?: string | null) {
  if (!isSupportedServerVersion(version)) {
    throw createUnsupportedServerVersionError(version);
  }
}

function mapServerConfigError(error: unknown) {
  const userFriendlyError = UserFriendlyError.fromAny(error);
  if (
    userFriendlyError.is('NETWORK_ERROR') ||
    userFriendlyError.is('REQUEST_ABORTED') ||
    userFriendlyError.is('TOO_MANY_REQUEST')
  ) {
    return userFriendlyError;
  }

  if (error instanceof Error) {
    const detail = `${error.name}: ${error.message}`;
    if (MISSING_SERVER_VERSION_PATTERNS.some(pattern => pattern.test(detail))) {
      return createUnsupportedServerVersionError();
    }

    if (NETWORK_ERROR_PATTERNS.some(pattern => pattern.test(detail))) {
      return new UserFriendlyError({
        status: 504,
        code: 'NETWORK_ERROR',
        type: 'NETWORK_ERROR',
        name: 'NETWORK_ERROR',
        message: detail,
        stacktrace: error.stack,
      });
    }
  }

  return userFriendlyError;
}

export class ServerConfigStore extends Store {
  constructor() {
    super();
  }

  async fetchServerConfig(
    serverBaseUrl: string,
    abortSignal?: AbortSignal
  ): Promise<ServerConfigType> {
    try {
      const gql = gqlFetcherFactory(
        `${serverBaseUrl}/graphql`,
        globalThis.fetch
      );
      const serverConfigData = await gql({
        query: serverConfigQuery,
        context: {
          signal: abortSignal,
          headers: {
            'x-affine-version': BUILD_CONFIG.appVersion,
          },
        },
      });
      assertSupportedServerVersion(serverConfigData.serverConfig.version);

      let branding: ServerBranding = DEFAULT_SERVER_BRANDING;
      try {
        const brandingData = await gql({
          query: serverBrandingQuery,
          context: {
            signal: abortSignal,
            headers: {
              'x-affine-version': BUILD_CONFIG.appVersion,
            },
          },
        });
        branding = normalizeServerBranding(brandingData.serverConfig);
      } catch {
        // Older AFFiNE servers do not expose the optional branding fields.
      }

      try {
        const extrasData = await gql({
          query: serverBrandingExtrasQuery,
          context: {
            signal: abortSignal,
            headers: {
              'x-affine-version': BUILD_CONFIG.appVersion,
            },
          },
        });
        branding = {
          ...branding,
          ...extrasData.serverConfig,
        };
      } catch {
        // Older AFFiNE servers do not expose the optional branding extras.
      }

      if (
        serverConfigData.serverConfig.features.includes(ServerFeature.OAuth)
      ) {
        const oauthProvidersData = await gql({
          query: oauthProvidersQuery,
          context: {
            signal: abortSignal,
            headers: {
              'x-affine-version': BUILD_CONFIG.appVersion,
            },
          },
        });
        return {
          ...mergeServerBranding(serverConfigData.serverConfig, branding),
          ...oauthProvidersData.serverConfig,
        };
      }
      return mergeServerBranding(serverConfigData.serverConfig, branding);
    } catch (error) {
      throw mapServerConfigError(error);
    }
  }
}
